import { Platform } from "@angular/cdk/platform";
import { Injectable } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { MatSnackBarRef } from "@angular/material/snack-bar";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { SnackBarComponentBase } from "@obg/mat.ui.core";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import {
	AppError,
	AUTH_ACTION_TYPE,
	AUTH_MESSAGE_CODES,
	CoreState,
	CustomerManagementSelector,
	OperatingSystemService,
	SESSION_ACTION_TYPE,
	AbstractTranslatableSnackBarService,
	TwoFactorAuthLoginFailPayload,
	TwoFactorAuthLoginSuccessPayload,
	TwoFactorAuthMethod,
	TwoFactorAuthLocalStorageService,
	SessionAction,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { DeviceConfigService, PlatformService } from "@obg/ngx.global";
import { capitalize, cloneDeep } from "lodash-es";
import { EMPTY, iif, Observable, of, Subject, timer } from "rxjs";
import { catchError, filter, map, switchMap, takeUntil, tap, withLatestFrom } from "rxjs/operators";

import { UserDeviceService } from "../utils/services/user-device.service";

import { TwoFactorAuthTelemetryService } from "./two-factor-auth-telemetry.service";
import { requestActionFail, requestOTPAction, TwoFactorAuthAction } from "./two-factor-auth.action";
import { TwoFactorAuthConfigService } from "./two-factor-auth.config";
import {
	BROWSER_LIST,
	CommunicationChannel,
	OperationType,
	TwoFactorAuthCodeCommonVerificationErrors,
	twoFactorAuthCodeVerificationErrorDictionary,
	TwoFactorAuthFlow,
	twoFactorAuthMessageDictionary,
	TwoFactorAuthMessageType,
	TwoFactorAuthMethodType,
	TwoFactorChallengeType,
	twoFactorSuccessMessageTypeTree,
} from "./two-factor-auth.const";
import { TwoFactorAuthDevice } from "./two-factor-auth.model";
import { TwoFactorAuthSelector } from "./two-factor-auth.selector";
import { TwoFactorAuthService } from "./two-factor-auth.service";

@Injectable()
export class TwoFactorAuthEffect {
	resetOnLogout$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => TwoFactorAuthAction.resetOnLogout())
		)
	);

	requestOneTimePassword$ = createEffect(() =>
		this.actions$
			.pipe(ofType<requestOTPAction>(TwoFactorAuthAction.requestOTP, TwoFactorAuthAction.retryRequestOTP))
			.pipe(
				withLatestFrom(
					this.store.select(CustomerManagementSelector.getCustomer),
					this.store.select(CustomerManagementSelector.getCredentials),
					this.store.select(TwoFactorAuthSelector.getSelectedMethod),
					this.store.select(TwoFactorAuthSelector.getLoginToken),
					this.store.select(TwoFactorAuthSelector.isSuspiciousLogin)
				),
				map(([{ payload }, customer, credentials, selectedMethod, loginToken, isSuspiciousLogin]) => {
					const communicationChannel =
						selectedMethod === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
							? CommunicationChannel.Email
							: CommunicationChannel.SMS;
					return {
						payload,
						customer,
						credentials,
						communicationChannel,
						loginToken,
						isSuspiciousLogin,
					};
				}),
				switchMap(({ payload, customer, communicationChannel, loginToken, isSuspiciousLogin }) =>
					this.twoFactorAuthService
						.postOneTimePasswordRequest(
							communicationChannel,
							payload?.authFlow === TwoFactorAuthFlow.LoginFlow ? loginToken : customer?.customerId,
							isSuspiciousLogin
								? OperationType.SuspiciousLogin
								: payload?.isPostRegistration
									? OperationType.RegistrationVerification
									: OperationType.TwoFactorVerification
						)
						.pipe(
							map((response) =>
								TwoFactorAuthAction.requestOTPSuccess({
									payload: response,
									communicationChannel,
								})
							),
							catchError((error: AppError) =>
								of(TwoFactorAuthAction.requestOTPFailure({ errorCode: error.code }))
							)
						)
				)
			)
	);

	requestOneTimePasswordSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.requestOTPSuccess),
			tap(({ payload }) => this.handleOtpValidity(payload.tokenValidUntil!)),
			switchMap(({ communicationChannel }) =>
				this.openSnackBar$(
					communicationChannel === CommunicationChannel.Email
						? TwoFactorAuthMessageType.EmailSendSuccess
						: TwoFactorAuthMessageType.SMSSendSuccess
				)
			),
			map(() => {
				if (this.isMethodSwitchingEnabled) {
					return TwoFactorAuthAction.resetOtpExpiredErrorCode();
				}
				/**
				 * BetssonBR will skip the login and method selection steps
				 * and goes directly to the OTP Email/SMS step, thus, we need to
				 * set the isBusy flag to false due to the beforementioned steps awaiting
				 */
				return TwoFactorAuthAction.setIsBusy({ isBusy: false });
			})
		)
	);

	verifyOneTimePassword$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.verifyOTP),
			switchMap(({ oneTimePassword }) =>
				this.twoFactorAuthService.sendOnetimePassword(oneTimePassword).pipe(
					map(() => TwoFactorAuthAction.verifyOTPSuccess()),
					catchError((error: AppError) => of(TwoFactorAuthAction.verifyOTPFailure({ errorCode: error.code })))
				)
			)
		)
	);

	verifyLoginPassword$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.verifyLoginPassword),
			switchMap((payload) =>
				this.twoFactorAuthService.verifyUsersPassword(payload.password).pipe(
					map((response) => TwoFactorAuthAction.verifyLoginPasswordSuccess({ response })),
					catchError((error: AppError) =>
						of(TwoFactorAuthAction.verifyLoginPasswordFailure({ errorCode: error.code }))
					)
				)
			)
		)
	);

	trackLoginPasswordVerifiedSuccessfully$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.verifyLoginPasswordSuccess),
				withLatestFrom(this.store.select(TwoFactorAuthSelector.getSelectedMethod)),
				map(([, method]) => method),
				tap((method) =>
					this.twoFactorAuthTelemetryService.trackLoginPasswordVerifiedSuccessfully({ method: method! })
				)
			),
		{ dispatch: false }
	);

	trackLoginPasswordVerificationFailed$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.verifyLoginPasswordFailure),
				withLatestFrom(this.store.select(TwoFactorAuthSelector.getSelectedMethod)),
				tap(([{ errorCode }, method]) =>
					this.twoFactorAuthTelemetryService.trackLoginPasswordVerificationFailed({
						method: method!,
						error: errorCode,
					})
				)
			),
		{ dispatch: false }
	);

	commonError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType<requestActionFail>(
					TwoFactorAuthAction.requestOTPFailure,
					TwoFactorAuthAction.operateAuthMethodFailure,
					TwoFactorAuthAction.deviceFailure,
					TwoFactorAuthAction.verifyOTPAccountNotVerifiedFailure
				),
				filter(({ errorCode }) => {
					const knownErrorCodes = Object.keys(twoFactorAuthCodeVerificationErrorDictionary);
					const isKnownErrorThrown = knownErrorCodes.includes(errorCode);
					return !isKnownErrorThrown;
				}),
				switchMap(() => this.openSnackBar$(TwoFactorAuthMessageType.CommonSendError))
			),
		{ dispatch: false }
	);

	operateAuthMethodSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.operateAuthMethodSuccess),
				withLatestFrom(this.store.select(TwoFactorAuthSelector.getCurrentDevice)),
				switchMap(([{ response, method, operationType }, currentDevice]) => {
					if (operationType === TwoFactorAuthMethodType.GetAll) {
						return EMPTY;
					}
					this.completeTimer();
					const communicationChannel =
						method === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
							? CommunicationChannel.Email
							: CommunicationChannel.SMS;

					return this.openSnackBar$(
						twoFactorSuccessMessageTypeTree[operationType](communicationChannel)!
					).pipe(
						switchMap((snackBarRef) =>
							snackBarRef.afterDismissed().pipe(
								filter(
									() => (response.length === 0 && currentDevice?.isEnabledRememberDevice) ?? false
								),
								tap(() => this.store.dispatch(TwoFactorAuthAction.forgetDevice()))
							)
						)
					);
				})
			),
		{ dispatch: false }
	);

	operateAuthMethod$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.operateAuthMethod),
			withLatestFrom(this.store.select(TwoFactorAuthSelector.getSelectedMethod)),
			// The `current-customer-security-options` endpoint is disabled for BetssonBR
			filter(() => this.config.getCurrentCustomerSecurityOptionsEnabled()),
			switchMap(([{ operationType, token }, selectedMethod]) =>
				this.twoFactorAuthService.operateAuthMethod(operationType, token, selectedMethod).pipe(
					map((response) =>
						TwoFactorAuthAction.operateAuthMethodSuccess({
							response,
							operationType,
							method: selectedMethod!,
						})
					),
					catchError((error: AppError) =>
						of(
							TwoFactorAuthAction.operateAuthMethodFailure({
								operationType,
								errorCode: error.code,
								method: selectedMethod!,
								remainingAttempts: error.data?.remainingAttempts,
							})
						)
					)
				)
			)
		)
	);

	trackVerificationCodeSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.operateAuthMethodSuccess),
				filter(({ operationType }) => operationType === TwoFactorAuthMethodType.Setup),
				withLatestFrom(this.store.select(TwoFactorAuthSelector.getEnabledMethods)),
				tap(([{ method, operationType }, enabledMethods]) => {
					this.twoFactorAuthTelemetryService.trackVerificationCodeSuccess({
						method,
						enabledMethods,
						operationType,
					});
				})
			),
		{ dispatch: false }
	);

	trackVerificationCodeFailure$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.operateAuthMethodFailure),
				withLatestFrom(this.store.select(TwoFactorAuthSelector.getEnabledMethods)),
				tap(([{ method, errorCode, operationType }, enabledMethods]) => {
					this.twoFactorAuthTelemetryService.trackVerificationCodeFailure({
						method,
						error: errorCode,
						enabledMethods,
						operationType,
					});
				})
			),
		{ dispatch: false }
	);

	trackTurningOffSingleMethodSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.operateAuthMethodSuccess),
				filter(({ operationType }) => operationType === TwoFactorAuthMethodType.Remove),
				tap(({ method, operationType }) => {
					this.twoFactorAuthTelemetryService.trackTurningOffSingleMethodSuccess({ method, operationType });
				})
			),
		{ dispatch: false }
	);

	trackTurningOffBothMethodsSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.operateAuthMethodSuccess),
				filter(({ operationType }) => operationType === TwoFactorAuthMethodType.RemoveAll),
				tap(() => this.twoFactorAuthTelemetryService.trackTurningOffBothMethodsSuccess())
			),
		{ dispatch: false }
	);

	loadCurrentDevice$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.loadCurrentDevice),
			filter(() => !this.platformService.isServer),
			withLatestFrom(this.store.select(TwoFactorAuthSelector.getCurrentDevice)),
			switchMap(([, device]) =>
				iif(
					() => this.config.getTwoFactorAuthEnabled() && !device,
					this.getCurrentDevice().pipe(
						map((currentDevice) => TwoFactorAuthAction.setCurrentDevice({ currentDevice }))
					),
					EMPTY
				)
			)
		)
	);

	forgetDevice$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.forgetDevice),
			withLatestFrom(this.store.select(TwoFactorAuthSelector.getCurrentDevice)),
			switchMap(([, currentDevice]) =>
				this.twoFactorAuthService.forgetDevice(currentDevice!.id).pipe(
					map(() => TwoFactorAuthAction.forgetDeviceSuccess()),
					catchError((error: AppError) => of(TwoFactorAuthAction.deviceFailure({ errorCode: error.code })))
				)
			)
		)
	);

	forgetDeviceSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(TwoFactorAuthAction.forgetDeviceSuccess),
				tap(() => {
					this.twoFactorAuthLocalStorageService.setIsEnabledRememberDevice(false);
				}),
				switchMap(() => this.openSnackBar$(TwoFactorAuthMessageType.RemoveRememberDeviceSuccess))
			),
		{ dispatch: false }
	);

	reAuthorizeUser$$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.reLoginUser),
			withLatestFrom(this.store.select(TwoFactorAuthSelector.getPassword)),
			map(([, password]) => TwoFactorAuthAction.verifyLoginPassword({ password: password! }))
		)
	);

	loginChallenge$$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter((payload) => payload.code === AUTH_MESSAGE_CODES.login.twoFactorAuthenticationRequired),
			map((response) => {
				const modifiedResponse = cloneDeep(response);
				modifiedResponse.data.challengeDetails.challengeType = TwoFactorChallengeType.RegularAuthentication;
				return modifiedResponse;
			}),
			map((response) => TwoFactorAuthAction.updateChallengeDetails(response.data))
		)
	);

	suspiciousLoginChallenge$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter(({ code }) => {
				const isCustomerNotAllowed = code === AUTH_MESSAGE_CODES.login.customerNotAllowed;
				const isSuspiciousLoginEnabled = this.config.getSuspiciousLoginEnabled();
				return isCustomerNotAllowed && isSuspiciousLoginEnabled;
			}),
			map((response) => {
				return TwoFactorAuthAction.updateChallengeDetails({
					...response.data,
					challengeDetails: {
						...response.data.challengeDetails,
						challengeType: TwoFactorChallengeType.SuspiciousLogin,
					},
				});
			})
		)
	);

	twoFactorVerifyOTPAccountNotVerified$ = createEffect(() =>
		this.actions$.pipe(
			ofType(TwoFactorAuthAction.verifyOTPAccountNotVerified),
			switchMap((payload) =>
				this.twoFactorAuthService.patchOneTimePasswordRequest(payload.twoFactorVerifyPayload).pipe(
					map(() => {
						if (
							payload.twoFactorVerifyPayload.operation === OperationType.RegistrationVerification &&
							payload.twoFactorVerifyPayload.loginToken
						) {
							return this.sessionAction.postRegistrationLoginWithAuthToken({
								authenticationToken: payload.twoFactorVerifyPayload.loginToken,
							});
						} else if (payload.twoFactorVerifyPayload.operation === OperationType.TwoFactorVerification) {
							this.matDialog.closeAll();
							return TwoFactorAuthAction.setEnabledMethods({
								methods: [
									{
										methodName: TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod,
									},
									{
										methodName: TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod,
									},
								],
							});
						}
						return TwoFactorAuthAction.verifyOTPFailure({
							errorCode: TwoFactorAuthCodeCommonVerificationErrors.InvalidServerResponse,
						});
					}),
					catchError((error: AppError) =>
						of(TwoFactorAuthAction.verifyOTPAccountNotVerifiedFailure({ errorCode: error.code }))
					)
				)
			)
		)
	);

	accountNotVerifiedChallenge$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter(
				({ code }) =>
					code === AUTH_MESSAGE_CODES.login.accountNotVerified && this.config.getPostRegistrationOTPEnabled()
			),
			map((response) => {
				return batchActions(
					TwoFactorAuthAction.updateChallengeDetails({
						challengeDetails: {
							token: response.data?.customerId,
							primaryCommunicationMethod: {
								channel: response.data?.channel,
								details: "",
							},
							challengeType: TwoFactorChallengeType.RegularAuthentication,
							remainingAttempts: response.data?.remainingAttempts,
							communicationMethods: [],
						},
						remainingAttempts: response.data?.remainingAttempts,
					}),
					TwoFactorAuthAction.resetErrorCode()
				);
			})
		)
	);

	isDeviceRemembered$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.config.getTwoFactorAuthEnabled()),
			withLatestFrom(this.store.select(TwoFactorAuthSelector.getCurrentDevice)),
			switchMap(([, currentDevice]) =>
				this.twoFactorAuthService.checkCustomerDevice(currentDevice!.id).pipe(
					map((isEnabled) => TwoFactorAuthAction.enableCurrentDevice({ isEnabled })),
					catchError((error: AppError) => of(TwoFactorAuthAction.deviceFailure({ errorCode: error.code })))
				)
			)
		)
	);

	trackLoginWithTwoFactorAuthSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.loginWithTwoFactorAuthSuccess),
				map((x: ActionWithPayload<TwoFactorAuthLoginSuccessPayload>) => x.payload),
				withLatestFrom(this.store.select(TwoFactorAuthSelector.isSuspiciousLogin)),
				tap(([{ method }, suspiciousFlow]) => {
					this.twoFactorAuthTelemetryService.trackLoginWithTwoFactorAuthSuccess({
						method,
						suspiciousFlow,
					});
				})
			),
		{ dispatch: false }
	);

	trackLoginWithTwoFactorAuthFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.loginWithTwoFactorAuthFail),
				map((x: ActionWithPayload<TwoFactorAuthLoginFailPayload>) => x.payload),
				withLatestFrom(this.store.select(TwoFactorAuthSelector.isSuspiciousLogin)),
				tap(([{ method, error }, suspiciousFlow]) => {
					this.twoFactorAuthTelemetryService.trackLoginWithTwoFactorAuthFail({
						method,
						error: error.code,
						suspiciousFlow,
					});
				})
			),
		{ dispatch: false }
	);

	private timer$: Subject<void> | undefined;
	isMethodSwitchingEnabled = this.config.getIsMethodSwitchingEnabled();

	constructor(
		private actions$: Actions,
		private snackbar: AbstractTranslatableSnackBarService,
		private store: Store<CoreState>,
		private twoFactorAuthService: TwoFactorAuthService,
		private operatingSystemService: OperatingSystemService,
		private deviceConfigService: DeviceConfigService,
		private config: TwoFactorAuthConfigService,
		private twoFactorAuthTelemetryService: TwoFactorAuthTelemetryService,
		private platformService: PlatformService,
		private platform: Platform,
		private twoFactorAuthLocalStorageService: TwoFactorAuthLocalStorageService,
		private userDeviceService: UserDeviceService,
		private sessionAction: SessionAction,
		private matDialog: MatDialog
	) {}

	private openSnackBar$(messageType: TwoFactorAuthMessageType): Observable<MatSnackBarRef<SnackBarComponentBase>> {
		const params = twoFactorAuthMessageDictionary[messageType];

		return this.snackbar.openSnackBar$(
			{
				message: params.message,
				type: params.type,
				duration: 4000,
				dismissible: true,
			},
			[TranslationGroup.Common]
		);
	}

	private completeTimer() {
		this.timer$?.next();
		this.timer$?.complete();
	}

	private handleOtpValidity(validUntil: string): void {
		this.completeTimer();
		this.timer$ = new Subject<void>();
		const validUntilDate = new Date(validUntil);

		timer(1000, 1000)
			.pipe(takeUntil(this.timer$!))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe(() => {
				if (validUntilDate.getTime() > Date.now()) {
					return;
				}

				this.store.dispatch(
					TwoFactorAuthAction.requestOTPFailure({
						errorCode: TwoFactorAuthCodeCommonVerificationErrors.ExpiredCode,
					})
				);
				this.completeTimer();
			});
	}

	private getCurrentDevice(): Observable<TwoFactorAuthDevice> {
		return this.userDeviceService.getUserDevice$().pipe(
			map(({ visitorId, components }) => {
				const device = this.deviceConfigService.getPlatform();
				const operationSystem = this.operatingSystemService.getOperatingSystem() ?? components.platform.value;
				const browsersFromFingerprintJs = components?.vendorFlavors?.value;
				const browsersFromPlatformService = BROWSER_LIST.filter(
					(browserName) => !!this.platform[browserName as keyof Platform]
				);
				const rawBrowserName = browsersFromFingerprintJs?.length
					? browsersFromFingerprintJs[0]
					: browsersFromPlatformService[0];
				const browser = rawBrowserName ? capitalize(rawBrowserName) : "Unknown browser";
				const isEnabledRememberDevice = this.twoFactorAuthLocalStorageService.getIsEnabledRememberDevice();

				return {
					id: visitorId,
					name: `${operationSystem} ${device} - ${browser}`,
					isEnabledRememberDevice,
				};
			})
		);
	}
}
